import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { resultsService, FilterResultsDto, ResultRow } from '../../services/results.service';
import { sortAptRows, AptDetailRow, AptSortKey, SortDirection } from '../../lib/aptSort';

interface AptDetailTableProps {
  filters: FilterResultsDto;
  /** 차트 타이틀(파일명 생성용) */
  titleForFile: string;
}

// 로컬(KST) 기준 YYYYMMDD — toISOString()의 UTC 변환으로 날짜가 밀리는 문제 방지
const localDateStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

const RESULT_TEXT: Record<number, string> = {
  0: '미열람',
  1: '메일열람',
  2: '링크클릭',
  3: '정보입력',
};

const RESULT_COLOR: Record<number, string> = {
  0: '#8B5CF6',
  1: '#F59E0B',
  2: '#EF4444',
  3: '#10B981',
};

// 컬럼 정의 — Confluence 요구사항: 회사 / 팀 / 이름 / 이메일 / 결과(Reported)
const COLUMNS: { key: AptSortKey; label: string }[] = [
  { key: 'company', label: '회사' },
  { key: 'team', label: '팀' },
  { key: 'name', label: '이름' },
  { key: 'email', label: '이메일' },
  { key: 'result', label: '결과' },
];

const AptDetailTable: React.FC<AptDetailTableProps> = ({ filters, titleForFile }) => {
  const [rows, setRows] = useState<AptDetailRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortKey, setSortKey] = useState<AptSortKey>('company');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  // 엑셀 다운로드 모달
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const data: ResultRow[] = await resultsService.getResults(filters);
      setRows(
        data.map((r) => ({
          company: r.company,
          team: r.team,
          name: r.name,
          email: r.email,
          result: r.result,
          reportCategory: r.reportCategory ?? null,
        })),
      );
    } catch (error) {
      console.error('Failed to load detail rows:', error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const sortedRows = useMemo(
    () => sortAptRows(rows, sortKey, sortDir),
    [rows, sortKey, sortDir],
  );

  const handleSort = (key: AptSortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIndicator = (key: AptSortKey) => {
    if (key !== sortKey) return ' ↕';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const handleExportClick = () => {
    setShowPasswordModal(true);
    setPassword('');
  };

  const handlePasswordSubmit = async () => {
    if (!password || password.length !== 4 || !/^\d{4}$/.test(password)) {
      alert('비밀번호는 4자리 숫자여야 합니다.');
      return;
    }
    setIsExporting(true);
    try {
      const blob = await resultsService.exportToExcel(filters, password);
      const fileName = `${titleForFile}_상세데이터_${localDateStr()}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowPasswordModal(false);
      setPassword('');
    } catch (error: any) {
      console.error('Export failed:', error);
      alert('엑셀 다운로드에 실패했습니다: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsExporting(false);
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setPassword('');
  };

  return (
    <div className="apt-detail">
      <div className="apt-detail-toolbar">
        <div className="apt-detail-count">
          총 <strong>{sortedRows.length}</strong>건
        </div>
        <button
          className="export-button"
          onClick={handleExportClick}
          disabled={isLoading || isExporting || sortedRows.length === 0}
        >
          {isExporting ? '다운로드 중...' : '엑셀 다운로드'}
        </button>
      </div>

      {isLoading ? (
        <div className="loading">데이터를 불러오는 중...</div>
      ) : sortedRows.length === 0 ? (
        <div className="no-data">조회된 데이터가 없습니다.</div>
      ) : (
        <div className="themed-table-wrap">
          <table className="themed-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="클릭하여 정렬"
                  >
                    {col.label}
                    <span style={{ opacity: col.key === sortKey ? 1 : 0.4, fontSize: '0.75rem' }}>
                      {sortIndicator(col.key)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.company || '-'}</td>
                  <td>{row.team || '-'}</td>
                  <td>{row.name || '-'}</td>
                  <td>{row.email || '-'}</td>
                  <td>
                    <span
                      style={{
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        background: `${RESULT_COLOR[row.result] ?? '#999'}20`,
                        color: RESULT_COLOR[row.result] ?? '#666',
                      }}
                    >
                      {RESULT_TEXT[row.result] ?? '미열람'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPasswordModal && (
        <div className="modal-overlay" onClick={handlePasswordCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>엑셀 다운로드</h3>
            <p>엑셀 파일을 암호화하여 다운로드합니다.</p>
            <input
              className="password-input"
              type="password"
              value={password}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                setPassword(value);
              }}
              placeholder="0000"
              maxLength={4}
              autoFocus
            />
            <div className="modal-buttons">
              <button onClick={handlePasswordCancel}>취소</button>
              <button onClick={handlePasswordSubmit} disabled={isExporting || password.length !== 4}>
                {isExporting ? '다운로드 중...' : '다운로드'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AptDetailTable;
